import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  SegmentedControl,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { recipesApi, recipeStatusMeta } from './recipesApi';
import MenuNavTabs from './MenuNavTabs';

const money = (cur, n) => `${cur || 'USD'} ${formatAmount(n)}`;
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);

const TYPE_FILTER = [
  { id: '', label: 'All' },
  { id: 'MenuItem', label: 'Menu items' },
  { id: 'Preparation', label: 'Sub-recipes' },
];

export default function RecipesPage() {
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission } = useProperty();
  const canManage = hasPermission('recipes.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await recipesApi.list({ search: search.trim() || undefined, type: type || undefined });
      setRows(data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, type]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const typeBadge = useCallback(
    (r) =>
      r.type === 'MenuItem' ? <Badge tone="info">{t('Menu item')}</Badge> : <Badge tone="neutral">{t('Sub-recipe')}</Badge>,
    [t],
  );

  const columns = useMemo(
    () => [
      { key: 'number', header: t('Code'), width: 120, render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{r.number}</span> },
      {
        key: 'name',
        header: t('Recipe'),
        render: (r) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{r.name}</span>
            {r.categoryName && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-foreground-subtle)' }}>{r.categoryName}</span>}
          </div>
        ),
      },
      { key: 'type', header: t('Type'), width: 130, render: typeBadge },
      {
        key: 'status', header: t('Status'), width: 110,
        render: (r) => { const m = recipeStatusMeta(r.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; },
      },
      { key: 'components', header: t('Items'), width: 80, align: 'right', render: (r) => r.componentCount },
      {
        key: 'cost', header: t('Cost / portion'), width: 140, align: 'right',
        render: (r) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {money(r.currency, r.costPerPortion)}
            {r.hasUncostable && <Icon.CircleAlert size={13} title={t('Some ingredients have no cost')} style={{ color: 'var(--color-warning)' }} />}
          </span>
        ),
      },
      {
        key: 'price', header: t('Sell'), width: 110, align: 'right',
        render: (r) => (r.sellingPrice != null ? money(r.currency, r.sellingPrice) : '—'),
      },
      {
        key: 'foodcost', header: t('Food cost %'), width: 120, align: 'right',
        render: (r) => {
          if (r.foodCostPercent == null) return '—';
          const high = r.foodCostPercent > 35;
          return <span style={{ color: high ? 'var(--color-danger)' : 'var(--color-success)', fontWeight: 600 }}>{pct(r.foodCostPercent)}</span>;
        },
      },
    ],
    [t, typeBadge],
  );

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Recipes')}
        description={t('Cost recipes and menu items from live ingredient prices, with sub-recipes, yields and food-cost %.')}
        icon={<Icon.ConciergeBell size={22} />}
        actions={
          canManage && (
            <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/recipes/new')}>
              {t('New recipe')}
            </Button>
          )
        }
      />

      <MenuNavTabs active="recipes" />

      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <div style={{ maxWidth: 320, flex: '1 1 240px' }}>
          <Input
            placeholder={t('Search recipes…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leadingIcon={<Icon.Search size={15} />}
          />
        </div>
        <SegmentedControl value={type} onChange={setType} options={TYPE_FILTER.map((o) => ({ ...o, label: t(o.label) }))} ariaLabel={t('Filter by type')} />
      </div>

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(r) => r.id}
          density="comfortable"
          onRowClick={(r) => navigate(`/recipes/${r.id}`)}
          emptyState={
            <EmptyState
              icon={<Icon.ConciergeBell size={28} />}
              title={t('No recipes yet')}
              description={t('Create recipes and menu items to track plate cost and food-cost margin.')}
              action={canManage && <Button variant="primary" onClick={() => navigate('/recipes/new')}>{t('New recipe')}</Button>}
            />
          }
        />
      )}
    </PageContainer>
  );
}
